import { useRef, useState } from 'react';
import { asyncApiCall } from '../../api';
import { finalizeVouchersApi } from '../apiOuigoRefunds';
import {
	classifyFinalizeResponse,
	mapVouchersToFinalizePayload,
} from '../mappers';

const submitVouchersRequest = (vouchers) =>
	asyncApiCall(finalizeVouchersApi, vouchers);

const getResponseMessages = (response) =>
	Array.isArray(response)
		? response
				.map(({ message }) => message)
				.filter(
					(message) => typeof message === 'string' && message.trim().length > 0,
				)
		: [];

const useVoucherSubmission = ({
	currentPageVouchers,
	onResultsRefresh,
	reloadAvailableVouchers,
	removeAvailableVouchers,
	submitVouchers = submitVouchersRequest,
}) => {
	const [selectedVouchers, setSelectedVouchers] = useState([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [result, setResult] = useState(null);
	const [messages, setMessages] = useState([]);
	const submittedVoucherIds = useRef([]);
	const selectedVoucherIds = selectedVouchers.map(({ id }) => id);

	const clearSelection = () => {
		setSelectedVouchers([]);
	};

	const handleVoucherSelectionChange = (voucherId, isSelected) => {
		const voucher = currentPageVouchers.find(({ id }) => id === voucherId);

		setSelectedVouchers((currentSelectedVouchers) => {
			if (!isSelected) {
				return currentSelectedVouchers.filter(({ id }) => id !== voucherId);
			}

			return voucher &&
				!currentSelectedVouchers.some(({ id }) => id === voucherId)
				? [...currentSelectedVouchers, voucher]
				: currentSelectedVouchers;
		});
	};

	const handleCurrentPageSelectionChange = (isSelected) => {
		const currentPageVoucherIds = currentPageVouchers.map(({ id }) => id);

		setSelectedVouchers((currentSelectedVouchers) => {
			if (!isSelected) {
				return currentSelectedVouchers.filter(
					({ id }) => !currentPageVoucherIds.includes(id),
				);
			}

			const selectedVoucherIdSet = new Set(
				currentSelectedVouchers.map(({ id }) => id),
			);

			return [
				...currentSelectedVouchers,
				...currentPageVouchers.filter(
					({ id }) => !selectedVoucherIdSet.has(id),
				),
			];
		});
	};

	const submit = async () => {
		if (selectedVoucherIds.length === 0 || isSubmitting) {
			return;
		}

		setIsSubmitting(true);
		setMessages([]);

		try {
			const finalizePayload = mapVouchersToFinalizePayload(selectedVouchers);
			const response = await submitVouchers(finalizePayload);
			const submissionResult = classifyFinalizeResponse(
				response,
				finalizePayload.length,
			);
			const responseMessages = getResponseMessages(response);

			setMessages(responseMessages);
			if (submissionResult === 'error') {
				setResult(responseMessages.length > 0 ? 'error' : null);
				return;
			}

			submittedVoucherIds.current = selectedVoucherIds;
			if (submissionResult === 'success') {
				clearSelection();
			}
			setResult(submissionResult);
		} catch (error) {
			const errorMessage = error?.errorResponse?.message || error?.message;

			setMessages(errorMessage ? [errorMessage] : []);
			setResult(errorMessage ? 'error' : null);
		} finally {
			setIsSubmitting(false);
		}
	};

	const closeResult = async () => {
		if (result === 'success' || result === 'partial') {
			try {
				const response = await reloadAvailableVouchers();

				if (result === 'success' && !response) {
					removeAvailableVouchers(submittedVoucherIds.current);
				}
			} catch {
				if (result === 'success') {
					removeAvailableVouchers(submittedVoucherIds.current);
				}
			}

			clearSelection();
			onResultsRefresh();
			submittedVoucherIds.current = [];
		}

		setResult(null);
		setMessages([]);
	};

	return {
		clearSelection,
		closeResult,
		handleCurrentPageSelectionChange,
		handleVoucherSelectionChange,
		isSubmitting,
		messages,
		result,
		selectedVoucherIds,
		submit,
	};
};

export default useVoucherSubmission;
